import { Checkbox, Dropdown, Tooltip } from 'antd';
import { useRef, useState } from 'react';
import './FileGridView.scss';
import { MoreOutlined } from '@ant-design/icons';
import type { FileActionDispatch, FileNode } from '../../../../types/FileManagerTypes';
import { useFileListSelection } from '../../../../hooks/useFileListSelection';
import { useAuth } from '../../../../context/AuthContext';
import {
  FileOptionsPanel,
  InlineRenameInput,
  NewFolderInput,
  dispatchRowAction,
  getFileOptions,
  getRowOptionsForFile,
  FileTypeIcon,
} from '../fileListShared';
import ConditionalRender from '../../../../shared/components/ConditionalRender';
import { EmptyState } from '../EmptyState/EmptyState';

const TileName = ({ name }: { name: string }) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [overflowed, setOverflowed] = useState(false);

  // Measure lazily on hover: show the full-name tooltip only when the name is
  // clipped by the standard CSS ellipsis.
  const checkOverflow = () => {
    const el = ref.current;
    if (el) setOverflowed(el.scrollWidth > el.clientWidth);
  };

  return (
    <Tooltip arrow={false} title={overflowed ? name : ''} mouseEnterDelay={0.55}>
      <span ref={ref} className="fgv-tile__name" onMouseEnter={checkOverflow}>
        {name}
      </span>
    </Tooltip>
  );
};

export interface FileGridViewProps {
  files: (FileNode | null | undefined)[];
  onFileAction: FileActionDispatch;
  onOpenFolder: (file: FileNode) => void;
  /** Fired on double-click for a file tile -- opens the details/preview modal. */
  onPreviewFile: (file: FileNode) => void;
  /** Persistent selection owned by VFSBrowser (shared with the list view). */
  selection: string[];
  setSelection: (ids: string[]) => void;
  /** id of a tile currently in inline-rename mode. */
  editingId?: string | null;
  onRequestRename?: (file: FileNode) => void;
  /** Inline rename committed via Enter. Return a string to show an inline error. */
  onCommitName?: (file: FileNode, value: string) => void | string | Promise<void | string | null>;
  /** Esc or blur (click outside) in the rename input -- caller clears edit mode. */
  onCancelRename?: (file: FileNode) => void;
  isTrash?: boolean;
  /**
   * When true, a dashed temporary-folder tile with a name input is prepended.
   * Wired by the (deferred) footer add-folder flow; false today.
   */
  creatingFolder?: boolean;
  /** Pre-filled default name for the temp-folder input (e.g. "New Folder (1)"). */
  newFolderDefaultName?: string;
  /** True while the create request is in flight -- disables the temp input. */
  submitting?: boolean;
  onCommitNewFolder?: (value: string) => void;
  onCancelNewFolder?: () => void;
  /** Current search term — used to determine which empty state to display. */
  searchTerm?: string;
  /** Whether a search is currently active — controls result-click behavior. */
  isSearchActive?: boolean;
  /** Whether filters are currently applied — takes precedence for the empty state. */
  isFilterActive?: boolean;
  /** Called when a search result tile is selected — clears search and navigates. */
  onSelectSearchResult?: (file: FileNode) => void;
}

export function FileGridView({
  files,
  onFileAction,
  onOpenFolder,
  onPreviewFile,
  selection,
  setSelection,
  editingId = null,
  onRequestRename,
  onCommitName,
  onCancelRename,
  isTrash = false,
  creatingFolder = false,
  newFolderDefaultName = 'New Folder',
  submitting = false,
  onCommitNewFolder,
  onCancelNewFolder,
  searchTerm = '',
  isSearchActive = false,
  isFilterActive = false,
  onSelectSearchResult,
}: FileGridViewProps) {
  const [dropdownOpenFileId, setDropdownOpenFileId] = useState<string | null>(null);
  const { canAccess } = useAuth();

  const { visibleFiles, isSelected, selectedCount, toggleSelection } = useFileListSelection({ files, selection, setSelection, onFileAction });

  const rowOptions = getFileOptions(isTrash, selectedCount > 1, canAccess);

  return (
    <div className="fgv-scroll">
      <div className="fgv-grid" role="grid" aria-label="Files">
        {creatingFolder && (
          <div className="fgv-tile fgv-tile--new" role="gridcell">
            <div className="fgv-tile__glyph">
              <img src="/assets/tile-folder-creation-icon.svg" alt="new folder" />
            </div>
            <div className="fgv-tile__footer">
              <NewFolderInput
                submitting={submitting}
                defaultName={newFolderDefaultName}
                creatingClassName="fgv-tile__creating"
                onCommit={(value) => onCommitNewFolder?.(value)}
                onCancel={() => onCancelNewFolder?.()}
              />
            </div>
          </div>
        )}

        {visibleFiles.length === 0 && !creatingFolder && (
          <EmptyState type={isFilterActive ? 'filter' : searchTerm.length > 0 ? 'search' : 'folder'} />
        )}
        {visibleFiles.map((file) => {
          const selected = isSelected(file.id);
          const editing = editingId === file.id;
          return (
            <div
              className={`fgv-tile${selected ? ' is-selected' : ''}${editing ? ' is-editing' : ''}`}
              role="gridcell"
              key={file.id}
              onDoubleClick={(e) => {
                e.stopPropagation();
                if (isSearchActive && onSelectSearchResult) {
                  onSelectSearchResult(file);
                } else {
                  file.isDir ? onOpenFolder(file) : onPreviewFile(file);
                }
              }}
            >
              <div className="fgv-tile__menu">
                <Dropdown
                  trigger={['click']}
                  placement="bottomRight"
                  open={dropdownOpenFileId === file.id}
                  onOpenChange={(open) => setDropdownOpenFileId(open ? file.id : null)}
                  dropdownRender={() => (
                    <FileOptionsPanel
                      options={getRowOptionsForFile(file, rowOptions)}
                      isMultiselect={selectedCount > 1}
                      onSelect={(actionId) =>
                        dispatchRowAction(
                          actionId,
                          file,
                          {
                            onFileAction,
                            onRequestRename,
                            onClose: () => setDropdownOpenFileId(null),
                          },
                          visibleFiles.filter((f) => isSelected(f.id)),
                        )
                      }
                    />
                  )}
                >
                  <div
                    className="fgv-dots-btn"
                    aria-label="File options"
                    onClick={(e) => e.stopPropagation()}
                    onDoubleClick={(e) => e.stopPropagation()}
                  >
                    <MoreOutlined />
                  </div>
                </Dropdown>
              </div>

              <div className="fgv-tile__glyph">
                <FileTypeIcon file={file} variant="tile" className="fgv-tile__icon" />
              </div>

              <div className="fgv-tile__footer">
                <Checkbox
                  className="custom-checkbox"
                  checked={selected}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => toggleSelection(file)}
                />
                <ConditionalRender
                  visible={!editing}
                  fallback={<InlineRenameInput file={file} onCommit={(f, value) => onCommitName?.(f, value)} onCancel={(f) => onCancelRename?.(f)} />}
                >
                  <TileName name={file.name} />
                </ConditionalRender>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
